/**
 * A query in one sentence: `describeQuery` says what a list of terms finds, as
 * parts a component can render (the term texts strong, the rest plain), in the
 * words of a label set. German is the default, English comes with it.
 *
 * Import it from `textQuery.ts`, which re-exports everything here.
 */

import { trimWhiteSpace, type TextQueryTerm } from './textQuery';

/** A piece of the sentence: plain text, or — `strong` — the text of a term. */
export interface DescribePart {
  text: string;
  strong?: boolean;
}

/**
 * Every word of the sentences, as templates. A `{name}` in a template is
 * replaced by what it names; the other characters are taken as they are. A
 * `{name}` that nothing fills stays as written, and a value no template asks
 * for is dropped. The German and the English set are listed at
 * {@link describeQuery}. Like `describeCondition`'s `andLabel` / `orLabel`,
 * any of them can be replaced by a consumer — one at a time (the rest stays
 * German), or all.
 *
 * A term is built up in layers: `quoted` wraps its text, a match template
 * wraps that, `caseSensitive` wraps the result. The templates of an exclusion
 * wrap the finished term, so a match template should read well after „nicht“
 * and „ohne“ (or „not“ and „without“).
 */
export interface DescribeQueryLabels {
  /** The sentence for a query with required terms and no exclusion. `{terms}`: the required terms. */
  findRequired: string;
  /** The sentence for a query with exclusions only. `{excluded}`: the exclusions. */
  findExcluded: string;
  /** The sentence for a query with both. `{terms}` and `{excluded}`. */
  findRequiredExcluded: string;
  /** A term text in quotes. `{text}`: the text — the one strong part, the quotes stay plain. */
  quoted: string;
  /** A term that may occur anywhere. `{term}`: the quoted text. */
  contains: string;
  /** A term at the start of a word. `{term}`: the quoted text. */
  startsWith: string;
  /** A term at the end of a word. `{term}`: the quoted text. */
  endsWith: string;
  /** A term as a sequence of words. `{term}`: the quoted text. */
  phrase: string;
  /** Added to a term that has to match in exactly that case. `{term}`: the term so far. */
  caseSensitive: string;
  /** The any-of group (an item of the list of required terms). `{terms}`: its members. */
  anyOf: string;
  /** An exclusion in the sentence without required terms. `{term}`: the term. */
  without: string;
  /** An exclusion in the sentence with required terms. `{term}`: the term. */
  not: string;
  /** Between the last two items of a list of required or excluded terms. */
  and: string;
  /** Between the last two members of the any-of group. */
  or: string;
  /** Between the other items of a list and of the any-of group. */
  separator: string;
}

/** The German sentences — the default. */
export const DESCRIBE_QUERY_LABELS_DE: Readonly<DescribeQueryLabels> = {
  findRequired: 'Findet Treffer, die {terms} enthalten.',
  findExcluded: 'Findet Treffer {excluded}.',
  findRequiredExcluded: 'Findet Treffer, die {terms} enthalten, aber {excluded}.',
  quoted: '„{text}“',
  contains: '{term}',
  startsWith: '{term} am Wortanfang',
  endsWith: '{term} am Wortende',
  phrase: '{term} als Wortfolge',
  caseSensitive: '{term} (genau so geschrieben)',
  anyOf: 'eins von {terms}',
  without: 'ohne {term}',
  not: 'nicht {term}',
  and: ' und ',
  or: ' oder ',
  separator: ', ',
};

/** The English sentences. */
export const DESCRIBE_QUERY_LABELS_EN: Readonly<DescribeQueryLabels> = {
  findRequired: 'Finds hits that contain {terms}.',
  findExcluded: 'Finds hits {excluded}.',
  findRequiredExcluded: 'Finds hits that contain {terms}, but {excluded}.',
  quoted: '"{text}"',
  contains: '{term}',
  startsWith: '{term} at the start of a word',
  endsWith: '{term} at the end of a word',
  phrase: '{term} as a phrase',
  caseSensitive: '{term} (exactly as written)',
  anyOf: 'one of {terms}',
  without: 'without {term}',
  not: 'not {term}',
  and: ' and ',
  or: ' or ',
  separator: ', ',
};

/** Appends a copy of `part`, joined to the plain part before it when both are plain; empty text is dropped. */
function push(out: DescribePart[], part: DescribePart): void {
  if (!part.text) return;
  const last = out[out.length - 1];
  if (last && !last.strong && !part.strong) last.text += part.text;
  else out.push(part.strong ? { text: part.text, strong: true } : { text: part.text });
}

/** A template with each `{name}` replaced by its parts. */
function fill(template: string, values: Readonly<Record<string, DescribePart[]>>): DescribePart[] {
  const out: DescribePart[] = [];
  let from = 0;
  for (const found of template.matchAll(/\{(\w+)\}/g)) {
    push(out, { text: template.slice(from, found.index) });
    const name = found[1]!;
    if (Object.hasOwn(values, name)) for (const part of values[name]!) push(out, part);
    else push(out, { text: found[0] });
    from = found.index + found[0].length;
  }
  push(out, { text: template.slice(from) });
  return out;
}

/** The items of a list, joined: `a, b and c`, with `last` before the last one. */
function join(items: readonly DescribePart[][], last: string, separator: string): DescribePart[] {
  const out: DescribePart[] = [];
  items.forEach((item, at) => {
    if (at > 0) push(out, { text: at === items.length - 1 ? last : separator });
    for (const part of item) push(out, part);
  });
  return out;
}

function resolve(labels: Partial<DescribeQueryLabels>): DescribeQueryLabels {
  const out = { ...DESCRIBE_QUERY_LABELS_DE };
  for (const key of Object.keys(out) as (keyof DescribeQueryLabels)[]) {
    const value = labels[key];
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}

function describeTerm(term: TextQueryTerm, labels: DescribeQueryLabels): DescribePart[] {
  const quoted = fill(labels.quoted, { text: [{ text: trimWhiteSpace(term.text), strong: true }] });
  const byMatch = fill(labels[term.match] ?? labels.contains, { term: quoted });
  return term.caseSensitive === true ? fill(labels.caseSensitive, { term: byMatch }) : byMatch;
}

/**
 * Says what a query finds, as one sentence in parts: the text of every term is
 * a `strong` part, quotes and all other words are plain. Joined, the parts are
 * the sentence. The quotes are **outside** the strong part, so a strong part is
 * exactly the term (trimmed) and can be linked, copied or compared.
 *
 * Disabled terms, and terms without a text, are left out; a query without any
 * left gives `[]`. One enabled `should` term counts as a required term (a group
 * of one). The required terms come first, in order, and the any-of group
 * (`should`) is the last of them; the exclusions (`mustNot`) follow.
 *
 * German (the default, {@link DESCRIBE_QUERY_LABELS_DE}):
 *
 * | Query | Sentence |
 * |---|---|
 * | required terms | `Findet Treffer, die „Dichtung“ und „TPE“ enthalten.` |
 * | three or more | `… „A“, „B“ und „C“ enthalten.` |
 * | exclusions only | `Findet Treffer ohne „PVC“.` (`… ohne „PVC“ und ohne „PE“.`) |
 * | both | `Findet Treffer, die „Dichtung“ und „TPE“ enthalten, aber nicht „PVC“.` |
 * | several exclusions | `… enthalten, aber nicht „PVC“ und nicht „PE“.` |
 * | any-of group | `Findet Treffer, die eins von „EPDM“ oder „TPE“ enthalten.` |
 * | required and group | `Findet Treffer, die „Dichtung“ und eins von „EPDM“ oder „TPE“ enthalten.` |
 * | `startsWith` / `endsWith` / `phrase` | `„dicht“ am Wortanfang` / `„dichtung“ am Wortende` / `„Dichtung TPE“ als Wortfolge` |
 * | `caseSensitive` | `„AB“ (genau so geschrieben)` |
 *
 * English ({@link DESCRIBE_QUERY_LABELS_EN}):
 *
 * | Query | Sentence |
 * |---|---|
 * | required terms | `Finds hits that contain "Dichtung" and "TPE".` |
 * | exclusions only | `Finds hits without "PVC".` (`… without "PVC" and without "PE".`) |
 * | both | `Finds hits that contain "Dichtung" and "TPE", but not "PVC".` |
 * | any-of group | `Finds hits that contain one of "EPDM" or "TPE".` |
 * | required and group | `Finds hits that contain "Dichtung" and one of "EPDM" or "TPE".` |
 * | `startsWith` / `endsWith` / `phrase` | `"dicht" at the start of a word` / `"dichtung" at the end of a word` / `"Dichtung TPE" as a phrase` |
 * | `caseSensitive` | `"AB" (exactly as written)` |
 *
 * The words are the {@link DescribeQueryLabels}; `labels` replaces any of them,
 * the rest stays German (an `undefined` entry is ignored).
 */
export function describeQuery(
  terms: readonly TextQueryTerm[],
  labels: Partial<DescribeQueryLabels> = {}
): DescribePart[] {
  const words = resolve(labels);
  const enabled = terms.filter((term) => !term.disabled && trimWhiteSpace(term.text) !== '');
  const shoulds = enabled.filter((term) => term.occur === 'should').length;
  const required: DescribePart[][] = [];
  const members: DescribePart[][] = [];
  const excluded: TextQueryTerm[] = [];
  for (const term of enabled) {
    if (term.occur === 'mustNot') excluded.push(term);
    else if (term.occur === 'should' && shoulds > 1) members.push(describeTerm(term, words));
    else required.push(describeTerm(term, words));
  }
  if (members.length) {
    const group = join(members, words.or, words.separator);
    required.push(fill(words.anyOf, { terms: group }));
  }
  if (!required.length && !excluded.length) return [];

  const list = (items: readonly DescribePart[][]): DescribePart[] =>
    join(items, words.and, words.separator);
  const exclusions = (template: string): DescribePart[] =>
    list(excluded.map((term) => fill(template, { term: describeTerm(term, words) })));
  if (!excluded.length) return fill(words.findRequired, { terms: list(required) });
  if (!required.length) return fill(words.findExcluded, { excluded: exclusions(words.without) });
  return fill(words.findRequiredExcluded, {
    terms: list(required),
    excluded: exclusions(words.not),
  });
}
